import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { useUi, cx } from "../ui";
import { formatMoney } from "../helpers";
import AddCardModal from "./AddCardModal";
import Tag from "./Tag";
import useSettleBalance from "../dashboard/useSettleBalance";
import { useInstallation } from "../../../components/InstallationContext";
import LoadingIndicator from "../../../components/LoadingIndicator";

const useStyles = makeStyles({
  box: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    fontSize: 13,
    background: T.elevated,
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginBottom: 2,
  },
  title: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: T.subtleFg,
    fontWeight: 600,
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: T.fg,
  },
  muted: { color: T.mutedFg, fontSize: 12 },
  total: {
    fontWeight: 700,
    borderTop: `1px solid ${T.borderSoft}`,
    paddingTop: 5,
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 4,
  },
  small: { padding: "6px 10px", fontSize: 12 },
  error: { color: T.danger, fontSize: 12 },
  /* Sits where the lines will be, so the box does not jump once they land. */
  loading: { display: "flex", justifyContent: "center", padding: "6px 0" },
});

const amount = (value) => Number(value ?? 0) || 0;

/* One word for the whole booking, in the order that matters to the desk. */
function statusWord(charge) {
  if (charge.settled) return { word: "Paid", tone: "muted" };
  if (charge.hotelInvoices) {
    return charge.posted
      ? { word: "Posted to room", tone: "muted" }
      : { word: "Hotel collection", tone: "info" };
  }
  if (charge.stayOutstanding > 0) {
    return { word: "Payment required", tone: "info" };
  }
  return { word: "Paid", tone: "muted" };
}

/* Who put the figure on the room, so the next person at the desk does not. */
function postedNote({ confirmedByName, confirmedRole, confirmedAt }) {
  const who = [confirmedByName, confirmedRole].filter(Boolean).join(" · ");
  const when = confirmedAt
    ? new Date(confirmedAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return ["Posted to room", who, when].filter(Boolean).join(" · ");
}

/*
 * "Pick-ups · 1 x $20, 2 included" - both halves named, because a guest who was
 * given two free ones should see that rather than an unexplained total. The
 * same line the guest's own page shows them.
 */
function pickupsLabel({ billed, free, unit }, currency) {
  const parts = [];

  if (billed > 0) {
    parts.push(
      unit > 0 ? `${billed} × ${formatMoney(unit, currency)}` : String(billed),
    );
  }

  if (free > 0) parts.push(`${free} included`);

  return `Pick-ups${parts.length ? ` · ${parts.join(", ")}` : ""}`;
}

/*
 * What the guest is being charged for, from the rate engine's own split.
 *
 * The pick-ups line counts the requests actually made, not the fee total: a
 * parked car is priced as though its pickup were already happening, and this
 * panel reports a balance rather than taking one. The pickup that has not been
 * lodged is quoted and taken by the request modal.
 */
function linesFor(charge) {
  const pickups = charge.pickups ?? { billed: 0, free: 0, total: 0, unit: 0, feesAud: 0 };
  const breakdown = charge.breakdown;

  /* Nothing on a chargeback card is collected here, so none of its lines can
     report a payment against them. */
  const pickupLine =
    pickups.total > 0
      ? {
          key: "pickup",
          label: pickupsLabel(pickups, charge.currency),
          value: pickups.feesAud,
          /* A booking whose pickups were all free still names them. */
          display: pickups.feesAud > 0 ? null : "Included",
          paid: charge.hotelInvoices
            ? 0
            : amount(breakdown?.valetRequestFeePaidAud),
        }
      : null;

  if (charge.hotelInvoices) {
    const invoice = charge.hotelInvoiced ?? {};

    return [
      { key: "stay", label: "Parking · hotel invoices the guest", value: amount(invoice.stayAud) },
      pickupLine,
      { key: "extras", label: "Extras", value: amount(invoice.extrasAud) },
    ].filter((line) => line && (line.value > 0 || line.display));
  }

  if (!breakdown) return [];

  return [
    {
      key: "parking",
      label: "Parking",
      value: amount(breakdown.bookingAud),
      paid: amount(breakdown.bookingPaidAud),
    },
    {
      key: "overage",
      label: "Overstay",
      value: amount(breakdown.overageAud),
      flatFee: amount(breakdown.overstayFlatFeeAud),
      paid: amount(breakdown.overagePaidAud),
    },
    pickupLine,
  ].filter((line) => line && (line.value > 0 || line.display));
}

/*
 * "$20 · $15 Paid", or just "Paid" once the line is settled. What has been
 * collected against a line is as much a part of reading it as what it cost.
 */
function paidNote(line, currency) {
  const paid = line.paid ?? 0;

  if (paid <= 0) return null;
  if (line.value > 0 && paid < line.value) {
    return `${formatMoney(paid, currency)} Paid`;
  }

  return "Paid";
}

/*
 * What is owed on this booking and whether it holds the car, as one compact
 * box rather than a call to action - the desk reads it far more often than it
 * acts on it, and a porter may only read it.
 */
export default function ChargesPanel({ charge, bookingId, hasBooking, canCharge }) {
  const s = useStyles();
  const ui = useUi();
  const { facility } = useInstallation() ?? {};
  const settlement = useSettleBalance(charge, bookingId);

  if (!hasBooking) {
    return (
      <div className={s.box} aria-label="Charges">
        <div className={s.head}>
          <span className={s.title}>Charges</span>
        </div>
        <div className={s.muted}>
          No booking on file for this car, so nothing can be priced or
          collected until it is matched.
        </div>
      </div>
    );
  }

  if (charge.loading) {
    return (
      <div className={s.box} aria-label="Charges" aria-busy="true">
        <div className={s.head}>
          <span className={s.title}>Charges</span>
        </div>
        <div className={s.loading}>
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  if (charge.error) {
    return (
      <div className={s.box} aria-label="Charges">
        <div className={s.head}>
          <span className={s.title}>Charges</span>
        </div>
        <div className={s.muted}>
          The balance could not be checked. Nothing is released until it is
          known.
        </div>
      </div>
    );
  }

  /* The stay is what this panel collects. Pickup fees are taken by the request
     that incurs them, so they never hold the car here. */
  const owes = charge.stayOutstanding > 0;
  const status = statusWord(charge);
  const lines = linesFor(charge);

  /* A porter is told whether the car is held and who can clear it, never the
     figures they cannot act on. */
  if (!canCharge) {
    return (
      <div className={s.box} aria-label="Charges">
        <div className={s.head}>
          <span className={s.title}>Charges</span>
          <Tag tone={owes ? "info" : "muted"} size={10}>
            {owes ? "Unpaid" : "Paid"}
          </Tag>
        </div>
        <div className={s.line}>
          <span>
            {owes
              ? "Unpaid charges hold this vehicle"
              : "Nothing holds this vehicle"}
          </span>
          <span className={s.muted}>{owes ? "Ask the desk" : ""}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={s.box} aria-label="Charges">
        <div className={s.head}>
          <span className={s.title}>Charges</span>
          <Tag tone={status.tone} size={10}>
            {status.word}
          </Tag>
        </div>

        {lines.length === 0 ? (
          <div className={s.muted}>Nothing to collect on this booking.</div>
        ) : (
          lines.map((line) => (
            <div key={line.key} className={s.line}>
              <span>{line.label}</span>
              <span>
                {line.display ?? formatMoney(line.value, charge.currency)}
                {line.flatFee > 0 && (
                  <span className={s.muted}>
                    {` (${formatMoney(line.flatFee, charge.currency)} flat fee)`}
                  </span>
                )}
                {paidNote(line, charge.currency) && (
                  <span className={s.muted}>
                    {" · "}
                    {paidNote(line, charge.currency)}
                  </span>
                )}
              </span>
            </div>
          ))
        )}

        {!charge.hotelInvoices && (
          <div className={cx(s.line, s.total)}>
            <span>Outstanding</span>
            <span>{formatMoney(charge.stayOutstanding, charge.currency)}</span>
          </div>
        )}

        {charge.settled && (
          <div className={s.muted}>
            {formatMoney(charge.settledAmount, charge.currency)} charged to the
            card on file.
          </div>
        )}

        <div className={s.line}>
          <span className={s.muted}>Collection</span>
          <span className={s.muted}>
            {owes && !charge.hotelInvoices
              ? "Held until payment"
              : "Permitted"}
          </span>
        </div>

        {charge.hotelInvoices && charge.posting && charge.posted && (
          <div className={s.muted}>
            {postedNote(charge.posting)}
          </div>
        )}

        {charge.payError && <div className={s.error}>{charge.payError}</div>}

        {charge.hotelInvoices && (
          <div className={s.actions}>
            <button
              type="button"
              className={cx(ui.primaryBtn, s.small)}
              disabled={charge.paying}
              onClick={charge.postToRoom}
            >
              {charge.paying
                ? "Posting…"
                : `${charge.posted ? "Post again" : "Posted to room"} · ${formatMoney(
                    amount(charge.postableTotal),
                    charge.currency,
                  )}`}
            </button>
          </div>
        )}

        {owes && !charge.hotelInvoices && (
          <div className={s.actions}>
            <button
              type="button"
              className={cx(ui.primaryBtn, s.small)}
              disabled={charge.paying}
              onClick={settlement.settle}
            >
              {charge.paying
                ? "Charging…"
                : `Take payment · ${formatMoney(
                    charge.stayOutstanding,
                    charge.currency,
                  )}`}
            </button>
            {charge.hasCardOnFile && bookingId && (
              <button
                type="button"
                className={cx(ui.mutedBtn, s.small)}
                onClick={settlement.openDifferentCard}
              >
                Use a different card
              </button>
            )}
          </div>
        )}
      </div>

      {settlement.addCardOpen && (
        <AddCardModal
          bookingId={bookingId}
          amountDue={charge.stayOutstanding}
          currency={charge.currency}
          country={facility?.city?.country}
          onClose={settlement.closeAddCard}
          onConfirmed={settlement.onCardAdded}
        />
      )}
    </>
  );
}
